/**
 * The bar that says you are looking at somebody else's screens.
 *
 * Not dismissible and not floating. It occupies real layout space above the
 * header, so the page is inset beneath it rather than sliding underneath —
 * a floating banner gets scrolled past, and the one thing that must not happen
 * here is forgetting which account you are reading.
 *
 * The mode is explicit because viewing and acting have different consequences.
 */
import { useNavigate } from 'react-router-dom';
import { Eye, LogOut } from 'lucide-react';
import { ROLE_LABELS } from '../store/auth';
import { useImpersonation } from '../store/impersonation';
import type { User } from '../types';

export default function ViewingAsBar() {
  const target = useImpersonation((s) => s.target);
  const stop = useImpersonation((s) => s.stop);
  const navigate = useNavigate();

  if (!target) return null;

  return (
    <div className="flex items-center justify-between gap-3 bg-amber-500 px-4 py-2 text-white">
      <div className="flex min-w-0 items-center gap-2">
        <Eye className="h-4 w-4 shrink-0" />
        <p className="truncate text-sm">
          Viewing as <span className="font-semibold">{target.name}</span>
          <span className="opacity-90">
            {' '}({ROLE_LABELS[target.role as User['role']] ?? target.role})
          </span>
          <span className="ml-2 rounded bg-white/20 px-1.5 py-0.5 text-xs font-medium">
            {target.mode === 'act' ? 'acting' : 'read only'}
          </span>
        </p>
      </div>
      <button
        type="button"
        className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-white/20 px-2.5 py-1 text-sm font-medium hover:bg-white/30"
        onClick={async () => {
          await stop();
          // Back to where the view was started from, at the person's row.
          navigate('/admin/users', { replace: true });
        }}
      >
        <LogOut className="h-3.5 w-3.5" />
        Exit
      </button>
    </div>
  );
}
